import { CalibrationReport, Devices } from "@app/shared";
import { useState } from "react";
import { Dialog, FovSweepHeatmaps } from ".";
import { parseFovSweepGrid } from "../../helpers/fovSweepHelpers";
import { useFetch } from "../../hooks";

type CalibrationReportsDialogProps = {
    show: boolean;
    deviceId: string | undefined;
    onClose: () => void;
};

// The generated model hydrates timestamps into Date objects even though the wire interface types them
// as strings, so accept either.
const formatDate = (value: Date | string | undefined) =>
    value ? new Date(value).toLocaleString() : "—";

const formatNumber = (value: number | undefined, digits = 2, suffix = "") =>
    value === undefined || value === null || Number.isNaN(value) ? "—" : `${value.toFixed(digits)}${suffix}`;

/** Pass/fail chip. A section that never ran shows as "skipped" rather than as a failure. */
const Verdict: React.FC<{ passed: boolean | undefined; label: string }> = ({ passed, label }) => {
    const style =
        passed === true
            ? "bg-green-100 text-green-800 border-green-300"
            : passed === false
              ? "bg-red-100 text-red-800 border-red-300"
              : "bg-gray-100 text-gray-500 border-gray-300";

    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${style}`}>
            {label}: {passed === true ? "pass" : passed === false ? "fail" : "skipped"}
        </span>
    );
};

/**
 * Anechoic-chamber calibration history for a device.
 *
 * These are manufacturing records — useful to whoever builds and diagnoses the hardware — so the caller
 * gates this behind a super-admin check.
 */
export const CalibrationReportsDialog: React.FC<CalibrationReportsDialogProps> = ({ show, deviceId, onClose }) => {
    const [reports, , helpers] = useFetch(
        () => (show && deviceId ? Devices.getCalibrationReports(deviceId) : Promise.resolve(undefined)),
        [show, deviceId],
    );
    const [expandedId, setExpandedId] = useState<string>();

    return (
        <Dialog
            show={show}
            title="Calibration Reports"
            onCancelClick={onClose}
            showOkButton={false}
            okButtonText="Close"
            className="w-full max-w-5xl"
        >
            <div className="max-h-[70vh] overflow-y-auto">
                {helpers.isLoading && <p className="p-4 text-sm text-gray-500">Loading…</p>}

                {!helpers.isLoading && (!reports || reports.length === 0) && (
                    <p className="p-4 text-sm text-gray-500">
                        This device has no calibration runs on record.
                    </p>
                )}

                {reports?.map((report: CalibrationReport) => {
                    const isExpanded = expandedId === report.id;
                    // Only parsed once the row is open: a history can hold many runs, and each payload
                    // carries a few hundred grid cells.
                    const sweepGrid = isExpanded ? parseFovSweepGrid(report.report) : undefined;

                    return (
                        <div key={report.id} className="mb-3 rounded-lg border border-gray-200">
                            <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`inline-block h-2.5 w-2.5 rounded-full ${
                                                report.passed ? "bg-green-500" : "bg-red-500"
                                            }`}
                                        />
                                        <span className="font-medium">{formatDate(report.createdUtc)}</span>
                                        <span className="text-xs text-gray-500">{report.chamberId}</span>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">
                                        {report.serialNumber && <>SN {report.serialNumber} · </>}
                                        FW {report.firmwareVersion ?? "—"} · Radar FW {report.radarFirmwareVersion ?? "—"} ·
                                        limits v{report.limitsVersion}
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    <Verdict passed={report.radarPassed} label="Radar" />
                                    <Verdict passed={report.accelerometerPassed} label="Accel" />
                                    <Verdict passed={report.fieldOfViewPassed} label="FOV" />
                                    <button
                                        className="text-xs text-blue-600 underline"
                                        onClick={() => setExpandedId(isExpanded ? undefined : report.id)}
                                    >
                                        {isExpanded ? "Hide detail" : "Detail"}
                                    </button>
                                </div>
                            </div>

                            {report.failureReason && (
                                <div className="border-t border-gray-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                                    {report.failureReason}
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-gray-200 p-3 text-sm sm:grid-cols-5">
                                <Metric label="Range bias" value={formatNumber(report.rangeBiasMeters, 4, " m")} />
                                <Metric label="Ch. imbalance" value={formatNumber(report.channelImbalance, 3)} />
                                <Metric label="Peak SNR" value={formatNumber(report.peakSnrDb, 1, " dB")} />
                                <Metric label="Predicted range" value={formatNumber(report.predictedMaxRangeMeters, 1, " m")} />
                                <Metric
                                    label="Dead cells"
                                    value={formatNumber(report.deadCellCount, 0)}
                                />
                            </div>

                            {isExpanded && (
                                <div className="border-t border-gray-200 p-3">
                                    {sweepGrid && (
                                        <div className="mb-4">
                                            <FovSweepHeatmaps grid={sweepGrid} />
                                        </div>
                                    )}

                                    {/* The full payload is deliberately raw: it carries the sample sweep and every
                                        acceptance check, and is the record engineering goes to when diagnosing a
                                        unit that came back from the field. */}
                                    <pre className="max-h-96 overflow-auto rounded bg-gray-50 p-2 text-[11px] leading-tight">
                                        {report.report ? formatJson(report.report) : "No detail recorded."}
                                    </pre>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Dialog>
    );
};

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
        <div className="font-mono text-sm">{value}</div>
    </div>
);

/** Pretty-prints the stored payload, falling back to the raw string if it is not valid JSON. */
const formatJson = (raw: string) => {
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
};
